import {
  FilterBar,
  type FilterFieldDef,
  type FilterOption,
  type FiltersState,
} from '@components/ui/FilterMenu';
import { MultiSelectEditorPanel } from '@components/ui/FilterMenu/editors/MultiSelectEditorPanel';
import { Card, CardContent, Divider, Stack, Typography } from '@mui/material';

import type { RevenueFilters } from './api';

export function RevenueFiltersBar({
  filters,
  dateFrom,
  dateTo,
  showDates,
  clinicians,
  cliniciansLoading,
  onFilters,
}: {
  filters: RevenueFilters;
  dateFrom: string;
  dateTo: string;
  showDates: boolean;
  clinicians: FilterOption[];
  cliniciansLoading?: boolean;
  onFilters: (patch: Partial<RevenueFilters>) => void;
}) {
  const clinicianField: FilterFieldDef = {
    id: 'provider',
    label: 'Clinician',
    type: 'custom',
    describeValue: value => ({
      op: 'is',
      value:
        value.kind === 'multiSelect'
          ? (clinicians.find(c => c.value === value.values[0])?.label ?? 'Selected clinician')
          : '',
    }),
    renderEditor: ({ value, onChange, onClose, autoFocus }) => (
      <MultiSelectEditorPanel
        def={{
          id: 'provider',
          label: 'Clinician',
          type: 'multiSelect',
          options: clinicians,
          loading: cliniciansLoading,
          searchPlaceholder: 'Search clinicians…',
        }}
        selected={value?.kind === 'multiSelect' ? value.values : []}
        onToggle={id => {
          onChange(
            value?.kind === 'multiSelect' && value.values.includes(id)
              ? null
              : { kind: 'multiSelect', values: [id] },
          );
          onClose();
        }}
        onClear={() => {
          onChange(null);
          onClose();
        }}
        autoFocusSearch={autoFocus}
      />
    ),
  };
  const fields: FilterFieldDef[] = [
    ...(showDates ? [{ id: 'dates', label: 'Submission date', type: 'dateRange' as const }] : []),
    clinicianField,
  ];
  const predicates: FiltersState = {};
  if (showDates) predicates.dates = { kind: 'dateRange', start: dateFrom, end: dateTo };
  if (filters.providerId)
    predicates.provider = { kind: 'multiSelect', values: [filters.providerId] };
  const reset = () => onFilters({ providerId: undefined, dateFrom: undefined, dateTo: undefined });
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Clean claims
          </Typography>
          <Divider />
          <FilterBar
            fields={fields}
            predicates={predicates}
            resultStatement="Claims submitted during the selected date range"
            canClearPredicates={Boolean(filters.providerId || filters.dateFrom || filters.dateTo)}
            canResetView={false}
            onClearPredicates={reset}
            onResetView={reset}
            onPredicatesChange={next => {
              const provider = next.provider;
              onFilters({
                providerId: provider?.kind === 'multiSelect' ? provider.values[0] : undefined,
                dateFrom:
                  next.dates?.kind === 'dateRange' ? (next.dates.start ?? undefined) : undefined,
                dateTo:
                  next.dates?.kind === 'dateRange' ? (next.dates.end ?? undefined) : undefined,
              });
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
